
import { useState, useEffect } from 'react';
import { CreditCard } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { CardFormFields } from './card/CardFormFields';
import { useCardFormValidation } from './card/useCardFormValidation';
import { useFormSubmission } from '@/hooks/useFormSubmission';
import { useNestedFormControl } from '@/hooks/useNestedFormControl';
import { ModalHeader, ModalFooter, ModalBody } from '@/components/ui/modal';

interface CardFormNestedProps {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void> | void;
  onSuccess?: () => void;
  initialData?: any;
  isNested?: boolean;
}

export const CardFormNested = ({
  onClose,
  onSubmit,
  onSuccess,
  initialData,
  isNested = true
}: CardFormNestedProps) => {
  const { t } = useTranslation();
  const { isSubmitting, handleSubmit, resetState } = useFormSubmission();
  const { startNestedForm, endNestedForm } = useNestedFormControl();
  
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    bank: initialData?.bank || '',
    limit: initialData?.limit || '',
    closingDate: initialData?.closingDate || '',
    dueDate: initialData?.dueDate || '',
    color: initialData?.color || '#8B5CF6',
    accountId: initialData?.accountId || ''
  });
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const {
    errors,
    nameError,
    validateForm,
    handleNameChange,
    getNameErrorType,
    getNameErrorMessage,
    resetValidation
  } = useCardFormValidation(initialData);

  useEffect(() => {
    setTouchedFields({});
    setHasSubmitted(false);
    if (isNested) {
      startNestedForm('card');
    }
    
    return () => {
      if (isNested) {
        endNestedForm();
      }
    };
  }, [isNested, startNestedForm, endNestedForm]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setHasSubmitted(true);
    
    if (!formData.accountId || formData.accountId.trim() === '') {
      const accountSelect = document.querySelector('[id="accountId"]') as HTMLElement;
      if (accountSelect) {
        accountSelect.focus();
        accountSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    const form = e.currentTarget as HTMLFormElement;
    
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    
    if (!validateForm(formData)) {
      return;
    }
    
    await handleSubmit(
      async () => {
        await onSubmit(formData);
      },
      () => {
        onSuccess?.();
          setFormData({
            name: '',
            bank: '',
            limit: '',
            closingDate: '',
            dueDate: '',
            color: '#8B5CF6',
            accountId: ''
          });
          setTouchedFields({});
          setHasSubmitted(false);
        resetValidation?.();
      },
      onClose
    );
  };

  const handleClose = () => {
    setFormData({
      name: initialData?.name || '',
      bank: initialData?.bank || '',
      limit: initialData?.limit || '',
      closingDate: initialData?.closingDate || '',
      dueDate: initialData?.dueDate || '',
      color: initialData?.color || '#8B5CF6',
      accountId: initialData?.accountId || ''
    });
    setTouchedFields({});
    setHasSubmitted(false);
    resetValidation?.();
    resetState();
    
    if (isNested) {
      endNestedForm();
    }
    
    onClose();
  };

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldBlur = (field: keyof typeof formData) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isNested) {
      e.stopPropagation();
      
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
    }
  };

  const title = initialData ? t('forms.card.title_edit') : t('forms.card.title_new');

  return (
    <div className="flex flex-col h-full">
      <ModalHeader
        title={title}
        description={t('forms.card.description')}
        icon={CreditCard}
        onClose={handleClose}
      />

      <ModalBody>
        <div onKeyDown={handleKeyDown}>
          <form 
            id="nested-card-form" 
            onSubmit={handleFormSubmit} 
            className="space-y-6"
            onKeyDown={handleKeyDown}
          >
            <CardFormFields
              formData={formData}
              errors={errors}
              nameError={nameError}
              onNameChange={(value) => {
                handleNameChange(value, setFormData);
              }}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
              getNameErrorType={(show) => getNameErrorType(formData, show ?? (hasSubmitted || touchedFields.name))}
              getNameErrorMessage={(show) => getNameErrorMessage(formData, show ?? (hasSubmitted || touchedFields.name))}
              touchedFields={touchedFields}
              hasSubmitted={hasSubmitted}
            />
          </form>
        </div>
      </ModalBody>

      <ModalFooter
        primaryAction={{
          label: isSubmitting ? 'Salvando...' : (initialData ? t('forms.card.button_update') : t('forms.card.button_create')),
          type: 'submit',
          formId: 'nested-card-form',
          isLoading: isSubmitting
        }}
        secondaryAction={{
          label: t('forms.card.button_cancel'),
          onClick: handleClose
        }}
      />
    </div>
  );
};
